/** @jsxImportSource @emotion/react */
import {
  Buttons,
  Dialogs,
  Select,
  Selectable,
  TextFields,
} from '@zip/business-components';
import { useFeatureFlagClient } from '@zip/feature-flag-react';
import * as Icons from '@zip/icons';
import { useFeatureFlagsReady, useMerchantData } from 'contexts';
import { FC, useEffect, useState } from 'react';
import { selectFormatter } from 'utils';
import * as styles from './MerchantSelector.styles';
import { MerchantSelectorProps } from './MerchantSelectorProps';
import Hidden from 'components/hidden';

const MerchantSelector: FC<MerchantSelectorProps> = ({
  entity = 'both',
  dense = false,
}) => {
  const {
    merchantIdentity,
    merchants,
    branches,
    updateCurrentMerchantId,
    updateCurrentBranchId,
    isUnlinkedUser,
    isLinkingUser,
    needsToReauth,
  } = useMerchantData();

  const featureFlagClient = useFeatureFlagClient();
  const featureFlagReady = useFeatureFlagsReady();

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  const handleClick = (): void => setIsDialogOpen(true);

  const handleClose = (): void => setIsDialogOpen(false);

  const sortEntities = (
    array: { id: number; name: string }[]
  ): { id: number; name: string }[] =>
    array?.slice()?.sort((a, b) =>
      // Display `All Branches` and `Web Store` at top, then sort alphabetically
      a.id < 0 || b.id < 0 || a.name?.toLowerCase() > b.name?.toLowerCase()
        ? 1
        : -1
    );

  const [merchantsToDisplay, setMerchantsToDisplay] = useState<Selectable[]>(
    selectFormatter(sortEntities(merchants), 'name', 'id')
  );
  const [branchesToDisplay, setBranchesToDisplay] = useState<Selectable[]>(
    selectFormatter(sortEntities(branches), 'name', 'id')
  );

  const handleMerchantIdChange = (newMerchantId: string): void => {
    updateCurrentBranchId(-1);
    updateCurrentMerchantId(Number(newMerchantId));
  };

  const handleBranchIdChange = (newBranchId: string): void =>
    updateCurrentBranchId(Number(newBranchId));

  useEffect(() => {
    setMerchantsToDisplay(
      selectFormatter(sortEntities(merchants), 'name', 'id')
    );
  }, [merchants]);

  useEffect(() => {
    setBranchesToDisplay(selectFormatter(sortEntities(branches), 'name', 'id'));
  }, [branches]);

  useEffect(() => {
    if (!featureFlagClient || !featureFlagReady) {
      return;
    }

    // Get current user using client.getUser()
    const currentUser = featureFlagClient.getUser();

    // Prepare new user properties
    const updatedUser = {
      ...currentUser,
      // eslint-disable-next-line camelcase
      user_properties: {
        ...currentUser?.user_properties,
        merchantId: merchantIdentity?.merchantId,
        companyId: merchantIdentity?.companyId,
      },
    };

    // Set user properties
    featureFlagClient.setUser(updatedUser);

    // Trigger variants to be updated
    featureFlagClient.fetch(updatedUser).then(() => {
      featureFlagReady.updateMerchantId(updatedUser.user_properties.merchantId);
    });
  }, [merchantIdentity?.merchantId]);

  return (
    merchantIdentity?.branchId &&
    !isUnlinkedUser() &&
    !isLinkingUser() &&
    !needsToReauth() && (
      <>
        <Hidden mdDown css={styles.noWrap}>
          <div css={styles.headerDropdowns}>
            {merchantsToDisplay &&
              merchantsToDisplay?.length > 1 &&
              (entity === 'both' || entity === 'merchant-only') &&
              (dense ? (
                <Select.Ghost
                  id="merchant-dropdown"
                  options={merchantsToDisplay}
                  css={styles.merchantDropdown}
                  onChange={(e): void => handleMerchantIdChange(e.target.value)}
                  value={merchantIdentity?.merchantId?.toString()}
                  fullWidth
                />
              ) : (
                <TextFields.DenseSelect
                  id="merchant-dropdown"
                  options={merchantsToDisplay}
                  css={styles.merchantDropdown}
                  onChange={(e): void => handleMerchantIdChange(e.target.value)}
                  value={merchantIdentity?.merchantId?.toString()}
                  variant="outlined"
                  fullWidth
                />
              ))}
            {branchesToDisplay &&
              branchesToDisplay?.length >= 1 &&
              (entity === 'both' || entity === 'branch-only') &&
              (dense ? (
                <Select.Ghost
                  id="branch-dropdown"
                  options={branchesToDisplay}
                  onChange={(e): void => handleBranchIdChange(e.target.value)}
                  value={merchantIdentity?.branchId?.toString()}
                  disabled={branches?.length === 1}
                  fullWidth
                />
              ) : (
                <TextFields.DenseSelect
                  id="branch-dropdown"
                  options={branchesToDisplay}
                  onChange={(e): void => handleBranchIdChange(e.target.value)}
                  value={merchantIdentity?.branchId?.toString()}
                  disabled={branches?.length === 1}
                  variant="outlined"
                  css={styles.outlineSelectStyle}
                  fullWidth
                />
              ))}
          </div>
        </Hidden>
        <Hidden mdUp>
          <Icons.Shop css={[styles.iconStyling]} onClick={handleClick} />
        </Hidden>
        <Dialogs.Basic
          open={isDialogOpen}
          title="Selector"
          onClose={handleClose}
          actions={
            <Buttons.Primary onClick={handleClose}>Close</Buttons.Primary>
          }
          mobileFullscreen
        >
          {merchantsToDisplay && merchantsToDisplay?.length > 1 && (
            <TextFields.DenseSelect
              id="merchant-dropdown"
              label="Select merchant"
              options={merchantsToDisplay}
              onChange={(e): void => handleMerchantIdChange(e.target.value)}
              value={merchantIdentity?.merchantId?.toString()}
              variant="outlined"
              css={styles.merchantDropdown}
              fullWidth
            />
          )}
          {branchesToDisplay && branchesToDisplay?.length >= 1 && (
            <TextFields.DenseSelect
              id="branch-dropdown"
              label="Select branch"
              options={branchesToDisplay}
              onChange={(e): void => handleBranchIdChange(e.target.value)}
              value={merchantIdentity?.branchId?.toString()}
              disabled={branches?.length === 1}
              variant="outlined"
              css={styles.outlineSelectStyle}
              fullWidth
            />
          )}
        </Dialogs.Basic>
      </>
    )
  );
};

export default MerchantSelector;
